import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { EmailApiService } from '@core/services/application/email/email-api.service';
import { ApiResponse } from '@models/api-response';
import { EmailSubscriptionModel } from '@models/email/email-subscription.model';
import { PagedResponse } from '@models/paged-response';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subscription, filter, map, shareReplay, switchMap, tap } from 'rxjs';
import { EmailNotificationsModalComponent } from './email-notifications-modal/email-notifications-modal.component';

@Component({
  selector: 'zbp-email-notifications',
  templateUrl: './email-notifications.component.html',
  styleUrls: ['./email-notifications.component.scss'],
  standalone: false
})
export class EmailNotificationsComponent implements OnInit, OnDestroy {
  emailsForm: FormGroup = this.formBuilder.group({
    emailAddress0: new FormControl(''),
  });
  savedEmailAddresses: string[] = [];
  modalRef: NgbModalRef;
  totalRecords: number = 0;
  totalPages: number = 0;
  pageSize: number = 25;
  pageNumber: number = 1;

  private subscriptions: Subscription[] = [];
  /**
   * Trigger Observable to fetch all email addresses
   */

  triggerNotificationsFetch$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  /**
   * Manages fetching and displaying all email addresses
   */
  allNotificationEmails$: Observable<EmailSubscriptionModel[]> = this.triggerNotificationsFetch$.pipe(
    filter((trigger: boolean) => !!trigger),
    switchMap(() => this.emailService.getAllNotificationEmailRecords(this.pageNumber, this.pageSize).pipe(
      map((response: PagedResponse<EmailSubscriptionModel[]>) => response.response),
    )),
    tap(() => this.triggerNotificationsFetch$.next(false)),
  );

  /**
   * Trigger Observable to save a single email address
   */
  triggerEmailSave$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  /**
   * Manages the retrieval of an email address from the form control
   */
  emailToSave$: Observable<string> = this.triggerEmailSave$.pipe(
    filter(emailAddressLine => !!emailAddressLine),
    map(emailAddressLine => this.emailsForm.controls[emailAddressLine].value)
  );

  /**
   * Manages the saving of a single email address
   */
  handleSaveEmail$: Observable<EmailSubscriptionModel> = this.emailToSave$.pipe(
    switchMap((emailToSave) => {
      const request = {
        emailAddress: emailToSave,
        initialOneRosterFilePlacement: true,
        oneRosterCriticalErrors: true,
        integrationsShutOffRequest: true,
      };
      return this.emailService.saveNotificationEmail(request).pipe(
        map((response: ApiResponse<EmailSubscriptionModel>) => {
          if (response.success) {
            this.triggerEmailSave$.next(null);
            this.savedEmailAddresses.push(response.response.emailAddress);
            this.triggerNotificationsFetch$.next(true);
            this.toastr.success('Email address changes saved successfully', 'Success');
            return response.response;
          }
          throw new Error(response.messages[0]);
        }),
        shareReplay()
      );
    }),
  );

  /**
   * Trigger Observable for deleting a single email address from the table
   */
  triggerEmailDelete$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  /**
   * Manages the deletion of a single email address from the table
   */
  emailDelete$: Observable<boolean> = this.triggerEmailDelete$.pipe(
    filter((emailAddress: string) => !!emailAddress),
    switchMap((emailAddress: string) => this.emailService.deleteEmailAddress(emailAddress).pipe(
      map(() => true),
    )),
    tap(() => {
      this.triggerEmailDelete$.next(null);
      this.emailsForm.reset();
      while (Object.keys(this.emailsForm.controls).length > 1) {
        const toRemove = Object.keys(this.emailsForm.controls)[1];
        this.emailsForm.removeControl(toRemove);
      }
      this.toastr.success('Email address successfully deleted');
      this.triggerNotificationsFetch$.next(true);
    }),
    shareReplay()
  );

  /**
   * Trigger Observable for adding a new form control and entering an additional email address
   */
  triggerAddEmail$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Handles adding a new form control to the form for adding an additional email address
   */
  handleAddEmail$ = this.triggerAddEmail$.pipe(
    filter(triggerAddEmail => !!triggerAddEmail),
    tap(() => {
      const numOfEmail: number = Object.keys(this.emailsForm.controls).length;
      this.emailsForm.addControl(`emailAddress${numOfEmail}`, new FormControl(''));
      this.triggerAddEmail$.next(false);
    }),
    shareReplay()
  );

  /**
   * Trigger Observable for modifying the type of emails associated with an address
   */
  triggerManage$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  /**
   * Manages the retrieval of the email address to be managed
   */
  notificationEmailRecord$: Observable<EmailSubscriptionModel> = this.triggerManage$.pipe(
    filter(triggerManage => !!triggerManage),
    switchMap((formKey) => {
      const emailAddress = this.emailsForm.controls[formKey].value;
      return this.emailService.getNotificationEmailRecord(emailAddress);
    })
  );

  /**
   * Handles opening the modal and populating the email type checkboxes
   */
  handleManage$ = this.notificationEmailRecord$.pipe(
    tap((notificationEmailRecord) => {
      this.modalRef = this.modalService.open(EmailNotificationsModalComponent, {
        size: 'lg',
      });
      const {
        emailAddress,
        initialOneRosterFilePlacement,
        oneRosterCriticalErrors,
        integrationsShutOffRequest,
      } = notificationEmailRecord;
      this.modalRef.componentInstance.emailPrefsForm.get('emailAddress').setValue(emailAddress);
      this.modalRef.componentInstance.emailPrefsForm.get('oneRosterFiles').setValue(initialOneRosterFilePlacement);
      this.modalRef.componentInstance.emailPrefsForm.get('oneRosterErrors').setValue(oneRosterCriticalErrors);
      this.modalRef.componentInstance.emailPrefsForm.get('turnOffDashboard').setValue(integrationsShutOffRequest);
    }),
    shareReplay()
  );

  constructor(
    private formBuilder: FormBuilder,
    private emailService: EmailApiService,
    private modalService: NgbModal,
    public activeModal: NgbActiveModal,
    private toastr: ToastrService,
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.handleAddEmail$.subscribe(),
      this.handleSaveEmail$.subscribe(),
      this.handleManage$.subscribe(),
      this.emailDelete$.subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * Used to display either Save or Manage on the button text
   */
  isManage(formKey: string): boolean {
    return (this.savedEmailAddresses.includes(this.emailsForm.controls[formKey].value));
  }
}
